import { Stack, Group, Text, Badge, Divider, Box } from "@mantine/core";

interface SpecimenDiagnosisData {
  specimenId: string;
  microscopicDescription: string;
  location?: string | null;
  color?: string | null;
  sizeCm?: string | null;
  sizeMm?: string | null;
  consistency?: string | null;
  [key: string]: any;
}

interface MicroscopicDescriptionWidgetProps {
  caseData: any;
  diagnosisDataMap: Map<string, SpecimenDiagnosisData>;
}

function resolveHtml(
  html: string,
  vars: Record<string, string | null | undefined>
): string {
  if (!html) return "";
  // Replace <span data-variable="name">{{name}}</span> wrappers
  let resolved = html.replace(
    /<span[^>]*data-variable="(\w+)"[^>]*>\{\{\1\}\}<\/span>/gi,
    (_, name) => {
      const val = vars[name];
      return val?.trim() ? val.trim() : `{{${name}}}`;
    }
  );
  // Replace remaining bare {{name}} tokens
  resolved = resolved.replace(/\{\{(\w+)\}\}/g, (_, name) => {
    const val = vars[name];
    return val?.trim() ? val.trim() : `{{${name}}}`;
  });
  return resolved;
}

export function MicroscopicDescriptionWidget({
  caseData,
  diagnosisDataMap,
}: MicroscopicDescriptionWidgetProps) {
  const specimens: any[] = caseData?.specimens ?? [];

  if (specimens.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No specimens
      </Text>
    );
  }

  const sorted = [...specimens].sort((a: any, b: any) =>
    (a.number || "").localeCompare(b.number || "")
  );

  return (
    <Stack gap={0}>
      {sorted.map((specimen: any, idx: number) => {
        const diagData = diagnosisDataMap.get(specimen.id);

        const patientName = caseData?.patient
          ? [caseData.patient.firstName, caseData.patient.lastName]
              .filter(Boolean)
              .join(" ") || null
          : null;

        const vars: Record<string, string | null | undefined> = {
          specimen_letter: specimen.number ?? null,
          source: specimen.source?.name ?? null,
          subsite: diagData?.location ?? specimen.subsite?.name ?? null,
          collection_method: specimen.collectionMethod ?? null,
          block_count:
            specimen.blocks?.length != null
              ? String(specimen.blocks.length)
              : null,
          LOCATION: diagData?.location ?? specimen.subsite?.name ?? null,
          location: diagData?.location ?? specimen.subsite?.name ?? null,
          color: diagData?.color ?? null,
          size_cm: diagData?.sizeCm ?? null,
          size_mm: diagData?.sizeMm ?? null,
          consistency: diagData?.consistency ?? null,
          case_number: caseData?.caseNumber ?? null,
          patient_name: patientName,
          date_collected: caseData?.dateCollected ?? null,
        };

        const resolvedHtml = resolveHtml(
          diagData?.microscopicDescription || "",
          vars
        );

        return (
          <Stack key={specimen.id} gap={0}>
            {idx > 0 && <Divider my="xs" />}

            <Group gap="xs" wrap="nowrap" mb={4}>
              <Badge
                size="sm"
                variant="filled"
                color="teal"
                style={{ flexShrink: 0 }}
              >
                {specimen.number || "?"}
              </Badge>
              <Text size="xs" fw={600} truncate>
                {[specimen.source?.name, specimen.subsite?.name]
                  .filter(Boolean)
                  .join(", ") || "Unknown source"}
              </Text>
            </Group>

            {resolvedHtml ? (
              <Box
                pl={4}
                style={{ lineHeight: 1.5 }}
                className="widget-rich-text"
                dangerouslySetInnerHTML={{ __html: resolvedHtml }}
              />
            ) : (
              <Text size="xs" c="dimmed" pl={4} fs="italic">
                No microscopic description recorded
              </Text>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}
